import Close from '@mdi/svg/svg/close.svg?react';
import Play from '@mdi/svg/svg/play.svg?react';
import Plus from '@mdi/svg/svg/plus.svg?react';
import { Clickable, Flex, MDIIcon, MDIIconButton } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import CameraThumbnail from 'components/CameraThumbnail/CameraThumbnail';
import { type DownloadOption } from 'components/PublicPages/types/camera.types';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, { useEffect, useState } from 'react';
import { useControllerState } from 'solox';
import useLocale from 'src/hooks/useLocale';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type ThumbnailType } from 'vd-constants';
import { type PlayingCamerasController } from '../VideoPlayer/controller/PlayingCamerasController';
import CameraDownloadButton from './CameraDownloadButton';
import './PlayerCameraItem.sass';

interface PlayerCameraItemProps {
  camera: CallhomeCamera;
  playingCamerasController: PlayingCamerasController;
  cameraThumbnailType?: ThumbnailType;
  multiPlayDisabled?: boolean;
  playCamera: (camera: CallhomeCamera) => void;
  onUpdatePlayerCamera?: (id: string) => void;
  downloadOption?: DownloadOption;
}

const PlayerCameraItem: React.FC<PlayerCameraItemProps> = (props) => {
  const {
    camera,
    playingCamerasController,
    cameraThumbnailType,
    multiPlayDisabled,
    playCamera,
    onUpdatePlayerCamera,
    downloadOption,
  } = props;

  const playingCameraIds = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds,
  );

  const locale = useLocale();

  const [active, setActive] = useState(
    () => !!playingCameraIds.find((entry) => entry.chCameraId === camera.id),
  );

  useEffect(() => {
    setActive(!!playingCameraIds.find((entry) => entry.chCameraId === camera.id));
  }, [playingCameraIds, camera.id]);

  const toggleActive = () => {
    if (!camera) {
      throw new Error('No camera to be set to active');
    }

    onUpdatePlayerCamera?.(camera.id);
    if (active) {
      AnalyticsLogger.removeCameraFromMultiview({ camera });
      playingCamerasController.removePlayingCamera(camera);
    } else {
      AnalyticsLogger.addCameraToMultiview({ camera });
      playingCamerasController.addPlayingCamera(camera);
    }

    setActive(!active);
  };

  const onPlayCamera = () => {
    if (!camera) {
      throw new Error('No camera to play');
    }

    onUpdatePlayerCamera?.(camera.id);
    playCamera(camera);
  };

  const playerCameraItemClass = classNames('player-camera-item', {
    active,
    'remove-camera': playingCameraIds.length > 1,
  });

  const cameraActionButtonClass = classNames('camera-action-button', {
    add: !active,
    remove: active,
    visible: (!active || playingCameraIds.length > 1) && !multiPlayDisabled,
  });

  const infoWrapperClass = classNames('info-wrapper', `mode-dark`);

  const actionIcon = active ? Close : Plus;
  const actionText = active ? locale.camera.removeCamera : locale.camera.addCamera;

  if (!camera) {
    return <div />;
  }

  return (
    <Flex className={playerCameraItemClass} flexDirection="column">
      {!active && (
        <MDIIconButton
          title={locale.camera.playCamera}
          className="play-button"
          icon={Play}
          iconSize={28}
          fill="var(--color-white)"
        />
      )}
      <Flex
        justifyContent="center"
        alignItems="center"
        className={cameraActionButtonClass}
        width="auto"
        onClick={toggleActive}
        role="button"
      >
        <MDIIcon icon={actionIcon} size={20} fill="var(--color-white)" />
        <label>{actionText}</label>
      </Flex>
      <Clickable onClick={active ? undefined : onPlayCamera} className="clickable-thumbnail">
        <CameraThumbnail
          cameraId={camera.id}
          thumbnail={camera.thumbnail}
          thumbnailType={cameraThumbnailType}
        />
      </Clickable>
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        flexDirection="column"
        className={infoWrapperClass}
      >
        <Flex justifyContent="space-between" className="camera-info-wrapper">
          <label className="camera-name" data-testid={camera.name}>
            {camera.name}
          </label>
          {downloadOption && <CameraDownloadButton downloadOption={downloadOption} />}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default React.memo(PlayerCameraItem);
